import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AudioLines,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  LogIn,
  Search,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useAuthRequired } from "@/contexts/AuthRequiredContext";
import { usePracticeSessions } from "@/hooks/use-practice-sessions";
import { useSavedVocabularyList } from "@/hooks/use-saved-vocabulary";
import { useStudyGroups } from "@/hooks/use-study-groups";
import { useAIConversations } from "@/hooks/use-ai-conversations";
import { useVocabularySets } from "@/hooks/use-vocabulary-sets";
import { useVocabularyNotes } from "@/hooks/use-vocabulary-notes";
import { usePersonalNotes } from "@/hooks/use-personal-notes";
import { useGamification } from "@/hooks/use-gamification";
import { useGamificationUIStore } from "@lyder-talk/core";
import { useTheme } from "@lyder-talk/core";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import AvatarMenu from "@/components/layout/AvatarMenu";
import { cn } from "@/lib/utils";
import { sidebarDividerClass, sidebarFooterTileClass, sidebarRowActiveClass, sidebarRowClass } from "@/lib/card-styles";
import {
  getNavItem,
  isNavItemActive,
  MY_SPACE_EMPTY_TEXT,
  MY_SPACE_GROUP_KEYS,
  MY_SPACE_PLAIN_ITEMS,
  MY_SPACE_SECTION,
  NAV_VISIBLE_SECTIONS,
  sectionsForAuth,
  visibleForAuth,
  type MySpaceGroupKey,
  type NavItemDef,
  type NavSectionDef,
} from "@/components/layout/nav-config";
import { useNavLabels } from "@/components/layout/useNavLabels";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/settings/LanguageSwitcher";
import { useGuardedNavigate } from "@/hooks/use-guarded-navigate";
import { useGlobalSearch } from "@/contexts/GlobalSearchContext";

const COLLAPSED_KEY = "lydertalk:global-nav-collapsed";


/**
 * Groups shown as expandable lists for signed-in learners. Names, paths, and
 * icons come from `nav-config` so they cannot drift from the flat rows.
 */
const MY_SPACE_GROUPS: NavItemDef[] = MY_SPACE_GROUP_KEYS.map(getNavItem).filter(
  (i): i is NavItemDef => Boolean(i),
);

/**
 * Auto-expand the group whose row is active for the current route. Falls back
 * to the declared `match` prefixes, so a new child route lights up its
 * parent without touching this function.
 */
function groupAutoExpanded(pathname: string, key: MySpaceGroupKey): boolean {
  const item = getNavItem(key);
  return item ? isNavItemActive(pathname, item) : false;
}

function readCollapsed(): boolean | null {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    if (raw === null) return null;
    return raw === "1";
  } catch (error) {
    if (error instanceof DOMException) return null;
    console.warn("Failed to read sidebar state", error);
    return null;
  }
}

const MAX_GROUP_ITEMS = 5;

/**
 * Collapse/expand control for the rail. Always the last thing in the footer:
 * full width with a centred label and the chevron pinned to the trailing edge.
 * Collapsed mode has no room for a label, so it falls back to a centred icon.
 */
function SidebarCollapseButton({
  collapsed,
  onClick,
}: {
  collapsed: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  // The verb goes through a translated key with the noun interpolated, rather
  // than string-concatenating an English verb onto a translated label.
  const label = collapsed
    ? t("nav.actions.expandSidebar", "Expand sidebar")
    : t("nav.actions.collapseSidebar", "Collapse sidebar");
  const Icon = collapsed ? ChevronsRight : ChevronsLeft;
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-expanded={!collapsed}
      className={cn(
        "relative flex h-9 w-full items-center justify-center text-xs font-medium text-muted-foreground transition-colors hover:text-foreground",
        collapsed ? "px-0" : cn(sidebarFooterTileClass, "px-2"),
      )}
    >
      {collapsed ? (
        <Icon className="h-4 w-4" />
      ) : (
        <>
          <span className="truncate">{label}</span>
          <Icon className="absolute right-2 h-4 w-4" />
        </>
      )}
    </button>
  );
}

/**
 * Section label — one rhythm for every group in the rail.
 *
 * More space above than below, so the label reads as the head of the group it
 * introduces rather than as a caption on the group above it. Extracted because
 * the rail has two renderers (flat rows and the expandable My Space groups) and
 * they used to carry two slightly different copies of this.
 */
function NavSectionLabel({
  id,
  children,
  className,
}: {
  id?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      id={id}
      className={cn(
        "px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The one navigation row shape.
 *
 * `py-3` makes a 44px row — the touch target the audit asked for, and calm
 * enough that a rail of ten does not read as a wall. `text-sm` is the documented
 * body step: the old 13px was a private size that drifted from the ramp.
 *
 * The focus ring is explicit because these are buttons with no border of their
 * own — keyboard users need to see where they are, and the rail is the first
 * thing they tab through.
 */
const navRowClass = cn(
  sidebarRowClass,
  // `w-full` matters: a row is a `<button>` inside an `<li>` now, and a button
  // shrinks to its content while a flex child stretches. Without it every row
  // collapsed to the width of its own label.
  "w-full !py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

/**
 * One search row.
 *
 * There are two of these because there are two questions a learner can ask the
 * library — "which topic should I study?" and "how is this word said?" — and a
 * single row called "Search" hides the second one behind a tab nobody opens.
 * Each row opens the same palette already on its own corpus, so the tab strip
 * and the rail always agree.
 */
function SidebarSearchRow({
  collapsed,
  icon: Icon,
  label,
  ariaLabel,
  shortcut,
  onClick,
}: {
  collapsed: boolean;
  icon: LucideIcon;
  label: string;
  ariaLabel?: string;
  shortcut?: string;
  onClick: () => void;
}) {
  if (collapsed) {
    return (
      <button
        onClick={onClick}
        title={shortcut ? `${label} (${shortcut})` : label}
        aria-label={ariaLabel ?? label}
        aria-keyshortcuts={shortcut ? "Meta+K Control+K" : undefined}
        data-tour={shortcut ? "nav-search-topics" : "nav-search-pronunciation"}
        className="relative flex h-11 w-11 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Icon className="h-[18px] w-[18px]" aria-hidden />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-label={ariaLabel ?? label}
      aria-keyshortcuts={shortcut ? "Meta+K Control+K" : undefined}
      data-tour={shortcut ? "nav-search-topics" : "nav-search-pronunciation"}
      className={cn(navRowClass, "text-muted-foreground")}
    >
      <Icon className="h-[18px] w-[18px] shrink-0 text-stone-500 dark:text-stone-400" aria-hidden />
      <span className="flex-1 truncate text-left">{label}</span>
      {shortcut && (
        <kbd className="shrink-0 rounded border border-stone-200 bg-white px-1 py-px font-mono text-[11px] leading-4 text-stone-500 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-400">
          {shortcut}
        </kbd>
      )}
    </button>
  );
}

interface GroupItem {
  id: string;
  label: string;
  active: boolean;
  onClick: () => void;
}

/**
 * "My Space" section with expandable per-feature item lists. Rendered only
 * for signed-in learners in expanded mode — guests and icon mode keep the
 * plain navigation rows (see MY_SPACE_SECTION).
 */
function MySpaceGroups({
  userId,
  children,
}: {
  userId: string | null;
  /** Rows with nothing to expand (Progress) that still belong to this section. */
  children?: React.ReactNode;
}) {
  const guardedNavigate = useGuardedNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation();
  const navLabels = useNavLabels();

  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const group of MY_SPACE_GROUPS) {
      initial[group.id] = groupAutoExpanded(window.location.pathname, group.id as MySpaceGroupKey);
    }
    return initial;
  });

  const { history, loading: topicsLoading } = usePracticeSessions(userId, {
    enabled: Boolean(open["my-topics"]),
  });
  const vocabQuery = useSavedVocabularyList({
    pageSize: MAX_GROUP_ITEMS + 1,
    enabled: Boolean(open.vocabulary),
  });
  const { data: collectionsData, isLoading: collectionsLoading } = useVocabularySets({
    page: 1,
    pageSize: MAX_GROUP_ITEMS + 1,
    filter: "all",
    sort: "newest",
    enabled: Boolean(open.collections),
  });
  const { notes: vocabNotes, isLoading: vocabNotesLoading } = useVocabularyNotes({
    limit: MAX_GROUP_ITEMS,
    includeContent: false,
    enabled: Boolean(open.notes),
  });
  const { notes: personalNotes, isLoading: personalNotesLoading } = usePersonalNotes({
    limit: MAX_GROUP_ITEMS,
    includeContent: false,
    enabled: Boolean(open.notes),
  });
  const { myGroups, loading: groupsLoading } = useStudyGroups(userId, {
    includePublicGroups: false,
    includeNotifications: false,
    includeMine: Boolean(open["study-groups"]),
  });
  const { conversations, loading: aiLoading } = useAIConversations({
    enabled: Boolean(open["ai-chat"]),
  });

  const vocabItems = vocabQuery.data?.pages.flatMap((p) => p.items).slice(0, MAX_GROUP_ITEMS) ?? [];
  const vocabTotal = vocabQuery.data?.pages[0]?.total ?? 0;

  const activeNoteId = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get("note");
    } catch {
      return null;
    }
  }, [location.search]);

  const recentNotes = useMemo(() => {
    const items: Array<{ id: string; label: string; kind: "vocabulary" | "personal"; updatedAt: string }> = [
      ...vocabNotes.map((n) => ({
        id: n.id,
        label: n.word ?? "Unknown word",
        kind: "vocabulary" as const,
        updatedAt: n.updated_at,
      })),
      ...personalNotes.map((n) => ({
        id: n.id,
        label: n.title?.trim() || "Untitled note",
        kind: "personal" as const,
        updatedAt: n.updated_at,
      })),
    ];
    return items.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, MAX_GROUP_ITEMS);
  }, [vocabNotes, personalNotes]);
  const notesLoading = vocabNotesLoading || personalNotesLoading;

  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const groupContent: Record<string, { items: GroupItem[]; loading: boolean; count: number }> = {
    "my-topics": {
      items: history.slice(0, MAX_GROUP_ITEMS).map((s) => ({
        id: s.id,
        label: s.topic,
        active: pathname === `/my-topics/edit/${s.id}`,
        onClick: () => void guardedNavigate(`/my-topics/edit/${s.id}`),
      })),
      loading: topicsLoading,
      count: history.length,
    },
    vocabulary: {
      items: vocabItems.map((v) => ({
        id: v.id,
        label: v.word,
        active: false,
        onClick: () => void guardedNavigate("/vocabulary"),
      })),
      loading: vocabQuery.isLoading,
      count: vocabTotal,
    },
    collections: {
      items: (collectionsData?.sets ?? []).slice(0, MAX_GROUP_ITEMS).map((s) => ({
        id: s.id,
        label: s.title,
        active: pathname === `/vocabulary/collections/${s.id}`,
        onClick: () => void guardedNavigate(`/vocabulary/collections/${s.id}`),
      })),
      loading: collectionsLoading,
      count: collectionsData?.total ?? 0,
    },
    notes: {
      items: recentNotes.map((n) => ({
        id: `${n.kind}-${n.id}`,
        label: n.label,
        active: n.id === activeNoteId && pathname.startsWith("/vocabulary/notes"),
        onClick: () => void guardedNavigate(`/vocabulary/notes?note=${n.id}&kind=${n.kind}`),
      })),
      loading: notesLoading,
      count: vocabNotes.length + personalNotes.length,
    },
    "study-groups": {
      items: myGroups.slice(0, MAX_GROUP_ITEMS).map((g) => ({
        id: g.id,
        label: g.name,
        active: pathname === `/study-groups/${g.id}` || pathname.startsWith(`/study-groups/${g.id}/`),
        onClick: () => void guardedNavigate(`/study-groups/${g.id}`),
      })),
      loading: groupsLoading,
      count: myGroups.length,
    },
    "ai-chat": {
      items: conversations.slice(0, MAX_GROUP_ITEMS).map((c) => ({
        id: c.id,
        label: c.title || "Untitled chat",
        active: pathname === `/my-topics/ai/${c.id}`,
        onClick: () => void guardedNavigate(`/my-topics/ai/${c.id}`),
      })),
      loading: aiLoading,
      count: conversations.length,
    },
  };

  return (
    <div>
      <NavSectionLabel id="nav-section-my-space">
        {navLabels.sectionLabel(MY_SPACE_SECTION)}
      </NavSectionLabel>
      <ul
        aria-labelledby="nav-section-my-space"
        className="flex flex-col gap-0.5 px-2"
      >
        {MY_SPACE_GROUPS.map((group) => {
          const headerActive = isNavItemActive(pathname, group);
          const isOpen = Boolean(open[group.id]);
          const groupLabel = navLabels.itemLabel(group);
          const { items, loading, count } = groupContent[group.id] ?? { items: [], loading: false, count: 0 };
          return (
            <li key={group.id}>
              <div
                className={cn(navRowClass, headerActive && sidebarRowActiveClass)}
              >
                <button
                  onClick={() => void guardedNavigate(group.path)}
                  aria-current={headerActive ? "page" : undefined}
                  data-tour={group.tour}
                  className="flex min-w-0 flex-1 items-center gap-2.5"
                  title={groupLabel}
                >
                  <group.icon
                    aria-hidden
                    className={cn(
                      "h-[18px] w-[18px] shrink-0 transition-colors",
                      headerActive ? "text-primary-text" : "text-stone-500 dark:text-stone-400",
                    )}
                  />
                  <span className={cn("truncate", headerActive && "font-semibold")}>{groupLabel}</span>
                </button>
                {!loading && count > 0 && (
                  <span className="shrink-0 rounded-full bg-stone-200/70 px-1.5 text-[11px] font-semibold tabular-nums text-stone-500 dark:bg-stone-700/60 dark:text-stone-400">
                    {count}
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(group.id);
                  }}
                  aria-label={navLabels.expandLabel(groupLabel, isOpen)}
                  aria-expanded={isOpen}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                >
                  <ChevronDown
                    className={cn("h-3.5 w-3.5 transition-transform duration-200", isOpen && "rotate-180")}
                  />
                </button>
              </div>
              {isOpen && (
                <div className="ml-6 mr-2 flex flex-col gap-px border-l border-stone-200/70 pb-1 pl-2.5 dark:border-stone-800">
                  {loading ? (
                    [0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="mx-0 my-0.5 h-4 animate-pulse rounded bg-stone-200/70 dark:bg-stone-700/50"
                        style={{ width: `${85 - i * 15}%` }}
                        aria-hidden
                      />
                    ))
                  ) : items.length === 0 ? (
                    <p className="px-2 py-1 text-[11px] text-muted-foreground/70">{MY_SPACE_EMPTY_TEXT[group.id as MySpaceGroupKey] ?? "Nothing yet"}</p>
                  ) : (
                    items.map((item) => (
                      <button
                        key={item.id}
                        onClick={item.onClick}
                        title={item.label}
                        className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <span
                          aria-hidden
                          className={cn(
                            "h-1 w-1 shrink-0 rounded-full",
                            item.active ? "bg-primary" : "bg-stone-300 dark:bg-stone-600",
                          )}
                        />
                        <span className={cn("truncate", item.active && "font-semibold text-primary-text")}>
                          {item.label}
                        </span>
                      </button>
                    ))
                  )}
                  {!loading && items.length > 0 && (
                    <button
                      onClick={() => void guardedNavigate(group.path)}
                      className="rounded-md px-2 py-1 text-left text-[11px] font-medium text-primary-text/90 transition-colors hover:text-primary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {t("common.actions.viewAll", "View all")}
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
        {children}
      </ul>
    </div>
  );
}

/**
 * Global left navigation — rendered on desktop (`md+`) for every app page.
 * Mobile is intentionally untouched (bottom nav + hamburger sheet stay).
 *
 * Self-contained: owns its collapse state (localStorage), its active-state
 * detection, and its footer (Sign In CTA for guests, notifications + avatar
 * for signed-in learners).
 */
export function GlobalNavSidebar() {
  const location = useLocation();
  const { t } = useTranslation();
  const { data: isAuthenticated, userId } = useAuth();
  const { requireAuth } = useAuthRequired();
  const guardedNavigate = useGuardedNavigate();
  const {
    currentStreak,
    level,
    xp,
    levelProgress,
    isLoading: isGamificationLoading,
  } = useGamification();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { setStreakModalOpen } = useGamificationUIStore();
  const { openSearch } = useGlobalSearch();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = readCollapsed();
    if (stored !== null) return stored;
    // My Topics routes already show a contextual history panel beside the
    // nav — start icon-only there so the content keeps its width.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/my-topics")) {
      return true;
    }
    return false;
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch (error) {
      if (!(error instanceof DOMException)) {
        console.warn("Failed to persist sidebar state", error);
      }
    }
  }, [collapsed]);

  // Expose the live sidebar width for floating viewport-anchored UI
  // (reaction pill, presence toasts, update banner) so they can sit right of
  // the sidebar instead of underneath it.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const apply = () => {
      document.documentElement.style.setProperty(
        "--global-nav-width",
        mq.matches ? (collapsed ? "4rem" : "14rem") : "0px",
      );
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [collapsed]);

  const go = (path: string) => () => void guardedNavigate(path);

  const navLabels = useNavLabels();

  const renderItem = (item: NavItemDef) => {
    const active = isNavItemActive(location.pathname, item);
    // Resolved by stable `id`, not by `item.name` — see useNavLabels.
    const label = navLabels.itemLabel(item);
    if (collapsed) {
      return (
        <button
          key={item.id}
          onClick={go(item.path)}
          title={label}
          aria-current={active ? "page" : undefined}
          data-tour={item.tour}
          className={cn(
            "relative flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            active
              ? "bg-primary/10 text-primary-text hover:bg-primary/15"
              : "text-muted-foreground hover:bg-primary/10 hover:text-primary",
          )}
        >
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary transition-opacity duration-200",
              active ? "opacity-100" : "opacity-0",
            )}
          />
          <item.icon className={cn("h-[18px] w-[18px]", item.iconClassName)} aria-hidden />
        </button>
      );
    }
    return (
      <button
        key={item.id}
        onClick={go(item.path)}
        aria-current={active ? "page" : undefined}
        data-tour={item.tour}
        className={cn(navRowClass, active && sidebarRowActiveClass)}
      >
        <item.icon
          aria-hidden
          className={cn(
            "h-[18px] w-[18px] shrink-0 transition-colors",
            active ? "text-primary-text" : "text-stone-500 dark:text-stone-400",
            item.iconClassName,
          )}
        />
        <span className={cn("truncate", active && "font-semibold")}>{label}</span>
      </button>
    );
  };

  /**
   * One section = one label + one list, tied together by `aria-labelledby`.
   *
   * The label used to be a bare `div`: sighted users saw four groups, screen
   * readers heard one undifferentiated run of "Home, Browse, Videos…" and could
   * not tell where "Learn" ended. A list per section gives the count and the
   * name in one announcement.
   */
  const renderSection = (section: NavSectionDef) => {
    const items = section.items;
    const label = collapsed ? undefined : navLabels.sectionLabel(section);
    const labelId = `nav-section-${section.id}`;
    return (
      <div key={section.id}>
        {label && <NavSectionLabel id={labelId}>{label}</NavSectionLabel>}
        <ul
          aria-labelledby={label ? labelId : undefined}
          className={cn(collapsed ? "flex flex-col items-center gap-1 pt-3" : "flex flex-col gap-0.5 px-2")}
        >
          {items.map((item) => (
            <li key={item.id}>{renderItem(item)}</li>
          ))}
        </ul>
      </div>
    );
  };

  const handleSignIn = () => {
    void requireAuth({ stage: "signin" });
  };

  const avatarGamificationProps = {
    level,
    xp,
    currentStreak,
    levelProgress,
    isLoading: isGamificationLoading,
  };

  const avatarThemeProps = { theme, resolvedTheme, setTheme };

  const avatarMenu = (stretch?: boolean) => (
    <AvatarMenu
      gamificationProps={avatarGamificationProps}
      themeProps={avatarThemeProps}
      onStreakClick={() => setStreakModalOpen(true)}
      stretch={stretch}
    />
  );

  return (
    <aside
      aria-label="Primary"
      className={cn(
        "sticky top-0 hidden h-screen shrink-0 flex-col border-r bg-stone-50/60 transition-[width] duration-200 motion-reduce:transition-none dark:bg-stone-900/60 md:flex",
        sidebarDividerClass,
        collapsed ? "w-16 items-center" : "w-56",
      )}
    >
      {/* Brand */}
      <div className={cn("flex items-center pb-1 pt-2", collapsed ? "justify-center px-0" : "gap-2 px-3")}>
        <button
          onClick={go("/")}
          title="Lyder Talk — Home"
          className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-primary/15 to-secondary/15 shadow-sm ring-1 ring-inset ring-primary/20 transition-transform hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100"
        >
          <img src="/icon.png" alt="Lyder Talk" className="h-6 w-6 object-contain" loading="eager" />
        </button>
        {!collapsed && (
          <span className="truncate font-display text-base font-semibold tracking-tight text-primary-strong dark:text-primary">
            Lyder Talk
          </span>
        )}
      </div>

      {/* Find — the two searches sit above the destinations they search, and
          under their own label so the rail reads as labelled groups instead of
          two rows floating under the brand. */}
      <div>
        {!collapsed && (
          <NavSectionLabel id="nav-section-find" className="pt-2">
            {t("nav.sections.find", "Find")}
          </NavSectionLabel>
        )}
        <ul
          aria-labelledby={collapsed ? undefined : "nav-section-find"}
          className={cn(
            collapsed ? "flex flex-col items-center gap-1 pt-1" : "flex flex-col gap-0.5 px-2",
          )}
        >
          <li>
            <SidebarSearchRow
              collapsed={collapsed}
              icon={Search}
              label={t("nav.search.topicsRow", "Search topics")}
              ariaLabel={t("nav.search.topicsAria", "Search topics")}
              shortcut="⌘K"
              onClick={() => openSearch("topics")}
            />
          </li>
          <li>
            <SidebarSearchRow
              collapsed={collapsed}
              icon={AudioLines}
              label={t("nav.search.pronunciationRow", "Pronunciation")}
              ariaLabel={t("nav.search.pronunciationAria", "Search how a word is pronounced in videos")}
              onClick={() => openSearch("pronunciation")}
            />
          </li>
        </ul>
        {/* Collapsed: the label is gone, so a hairline is what separates the two
            tools from the destinations below them. */}
        {collapsed && (
          <div className="mx-auto mt-1 h-px w-8 bg-stone-200 dark:bg-stone-800" aria-hidden />
        )}
      </div>

      {/* Nav sections — one order for every auth state: Learn → My Space →
          Community. Only the My Space *treatment* differs (expandable groups for
          signed-in learners in expanded mode, plain rows otherwise). */}
      <nav className={cn("min-h-0 flex-1 overflow-y-auto pb-2", collapsed && "w-full")}>
        {sectionsForAuth(NAV_VISIBLE_SECTIONS, isAuthenticated).map((section) => {
          if (section.id !== "my-space") {
            return renderSection(section);
          }
          if (isAuthenticated && !collapsed) {
            return (
              <div key={section.id}>
                {/* Rows with nothing to expand join the same list, so the section
                    is one labelled group rather than a list plus an orphan. */}
                <MySpaceGroups userId={userId}>
                  {visibleForAuth(MY_SPACE_PLAIN_ITEMS, isAuthenticated).map((item) => (
                    <li key={item.id}>{renderItem(item)}</li>
                  ))}
                </MySpaceGroups>
              </div>
            );
          }
          return renderSection(section);
        })}
      </nav>

      {/* Footer */}
      <div className={cn("shrink-0 border-t border-stone-200/70 p-1.5 dark:border-stone-800", collapsed && "flex flex-col items-center gap-1")}>
        {isAuthenticated ? (
          collapsed ? (
            <>
              {avatarMenu()}
              <NotificationBell userId={userId} compact />
              <SidebarCollapseButton collapsed onClick={() => setCollapsed(false)} />
            </>
          ) : (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 px-0.5">
                {avatarMenu(true)}
                <NotificationBell
                  userId={userId}
                  compact
                  className={cn(sidebarFooterTileClass, "h-11 w-11 shrink-0 text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200")}
                />
              </div>
              <SidebarCollapseButton collapsed={false} onClick={() => setCollapsed(true)} />
            </div>
          )
        ) : collapsed ? (
          <>
            {/* Icon-only rail: the switcher reduces to its globe, with the
                active language in the tooltip and the aria-label. */}
            <div className="flex flex-col items-center">
              <LanguageSwitcher variant="compact" />
            </div>
            <button
              onClick={handleSignIn}
              title={t("common.actions.signIn", "Sign In")}
              aria-label={t("common.actions.signIn", "Sign In")}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-strong text-primary-foreground shadow-md shadow-primary/25 transition-colors hover:brightness-110"
            >
              <LogIn className="h-4 w-4" />
            </button>
            <SidebarCollapseButton collapsed onClick={() => setCollapsed(false)} />
          </>
        ) : (
          <div className="flex flex-col gap-1.5">
            {/* Signed-out visitors have no avatar menu to hang the switcher on,
                and they are exactly the people who most need it — a reader who
                cannot read the app cannot ask for it in English. So it sits in
                the sidebar footer, above the sign-in action. */}
            <div className="flex justify-center py-0.5">
              <LanguageSwitcher variant="compact" />
            </div>
            <button
              onClick={handleSignIn}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary-strong text-sm font-semibold text-primary-foreground shadow-md shadow-primary/25 transition-colors hover:brightness-110"
            >
              <LogIn className="h-4 w-4" />
              <span>{t("common.actions.signIn", "Sign In")}</span>
            </button>
            <SidebarCollapseButton collapsed={false} onClick={() => setCollapsed(true)} />
          </div>
        )}
      </div>
    </aside>
  );
}

export default GlobalNavSidebar;
